// Safe localStorage access.
//
// iOS Safari (Private Browsing, "Block All Cookies", and some in-app webviews) throws a
// SecurityError merely on *touching* window.localStorage, and throws QuotaExceededError far
// more readily than Chromium (much smaller quota). A `typeof window !== 'undefined'` check does
// NOT prevent either throw — `window` exists; the storage access itself is what fails. Because
// many of these reads run inside useState initializers (i.e. during render) and the app has no
// React error boundary on the hot paths, an uncaught throw becomes a blank white screen on
// iPhone while Chromium/Opera silently tolerate the same code.
//
// These wrappers swallow the failure so a blocked/full store degrades gracefully — a read
// returns null (same shape as a missing key) and a write becomes a no-op — instead of crashing
// render. Mirrors the try/catch pattern already used in I18nClientProvider and TutorialBanner.

export function safeGetItem(key) {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

export function safeSetItem(key, value) {
  try {
    if (typeof window === 'undefined') return false
    // Native setItem coerces non-string values to string; preserve that behaviour.
    window.localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

export function safeRemoveItem(key) {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.removeItem(key)
  } catch {
    // ignore — nothing to clean up if storage is unavailable
  }
}

// sessionStorage variants. Safari blocks sessionStorage on exactly the same settings it blocks
// localStorage on, and throws on the same bare property access — so anything reading it during
// render, in an effect, or in a timer callback needs the same wrapper.

export function safeSessionGetItem(key) {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

export function safeSessionSetItem(key, value) {
  try {
    if (typeof window === 'undefined') return false
    window.sessionStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

// Read + JSON.parse in one guarded step. safeGetItem only protects the storage
// access — a corrupt/legacy stored value still makes a bare JSON.parse throw, and
// inside a useState initializer that crashes the whole provider into the error
// boundary on every visit (nothing ever repairs the bad key). Falls back on a
// missing key, a parse error, or a parsed null.
export function safeGetJSON(key, fallback = null) {
  try {
    const raw = safeGetItem(key)
    if (raw === null) return fallback
    const parsed = JSON.parse(raw)
    return parsed ?? fallback
  } catch {
    return fallback
  }
}
